import React, { useEffect, useState } from "react";
import Title from "../../extra/Title";
import Pagination from "../../extra/Pagination";
import { useDispatch, useSelector } from "react-redux";
import { getGiftTransaction, getTransactionStats } from "../../store/giftTransaction/giftTransaction.action";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import dayjs from "dayjs";

const GiftTransaction = () => {
  const dispatch = useDispatch();
  const { giftTransaction, stats } = useSelector((state) => state.giftTransaction);

  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Server-paged (2026-09-30): fetch just the page being viewed.
  useEffect(() => {
    let alive = true;
    Promise.resolve(dispatch(getGiftTransaction(currentPage, rowsPerPage))).then((t) => {
      if (alive) {
        setTotal(Number(t) || 0);
        setIsLoading(false);
      }
    });
    return () => {
      alive = false;
    };
  }, [dispatch, currentPage, rowsPerPage]);

  useEffect(() => {
    dispatch(getTransactionStats());
  }, [dispatch]);

  useEffect(() => {
    setData(giftTransaction);
  }, [giftTransaction]);

  const indexOfLastPost = currentPage * rowsPerPage;
  const indexOfFirstPost = indexOfLastPost - rowsPerPage;
  const currentPosts = data; // already one server page

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <>
      <div className="mainCategory">
        <Title name="Gift Transactions" />

        {/* Statistics Cards */}
        <div className="row mb-4 mt-3">
          <div className="col-12 col-md-3 mb-3">
            <div className="card bg-gradient-primary text-white">
              <div className="card-body">
                <h6 className="card-subtitle mb-2 opacity-75">Total Transactions</h6>
                <h3 className="card-title mb-0">
                  {isLoading ? <Skeleton width={80} /> : stats.totalTransactions || 0}
                </h3>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-3 mb-3">
            <div className="card bg-gradient-success text-white">
              <div className="card-body">
                <h6 className="card-subtitle mb-2 opacity-75">Total Revenue (Coins)</h6>
                <h3 className="card-title mb-0">
                  {isLoading ? <Skeleton width={100} /> : (stats.totalRevenue || 0).toLocaleString()}
                </h3>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-3 mb-3">
            <div className="card bg-gradient-warning text-white">
              <div className="card-body">
                <h6 className="card-subtitle mb-2 opacity-75">Platform Commission</h6>
                <h3 className="card-title mb-0">
                  {isLoading ? <Skeleton width={100} /> : (stats.platformCommission || 0).toLocaleString()}
                </h3>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-3 mb-3">
            <div className="card bg-gradient-info text-white">
              <div className="card-body">
                <h6 className="card-subtitle mb-2 opacity-75">Creator Earnings</h6>
                <h3 className="card-title mb-0">
                  {isLoading ? <Skeleton width={100} /> : (stats.creatorEarnings || 0).toLocaleString()}
                </h3>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12 col-md-12 mt-3">
            <div className="customTable">
              <div className="primeMain">
                <table width="100%" className="primeTable text-center">
                  <thead className="sticky-top" style={{ top: "-1px", zIndex: "1" }}>
                    <tr>
                      <th className="fw-bold py-3" style={{ width: "4%" }}>No</th>
                      <th className="fw-bold py-3" style={{ width: "10%" }}>Date</th>
                      <th className="fw-bold py-3" style={{ width: "12%" }}>Sender</th>
                      <th className="fw-bold py-3" style={{ width: "12%" }}>Receiver</th>
                      <th className="fw-bold py-3" style={{ width: "12%" }}>Gift</th>
                      <th className="fw-bold py-3" style={{ width: "6%" }}>Qty</th>
                      <th className="fw-bold py-3" style={{ width: "8%" }}>Total</th>
                      <th className="fw-bold py-3" style={{ width: "8%" }}>Platform</th>
                      <th className="fw-bold py-3" style={{ width: "8%" }}>Creator</th>
                      <th className="fw-bold py-3" style={{ width: "10%" }}>Content</th>
                      <th className="fw-bold py-3" style={{ width: "10%" }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <>
                        {Array.from({ length: 10 }).map((_, index) => (
                          <tr key={index}>
                            <td><Skeleton /></td>
                            <td><Skeleton /></td>
                            <td><Skeleton /></td>
                            <td><Skeleton /></td>
                            <td><Skeleton /></td>
                            <td><Skeleton /></td>
                            <td><Skeleton /></td>
                            <td><Skeleton /></td>
                            <td><Skeleton /></td>
                            <td><Skeleton /></td>
                            <td><Skeleton /></td>
                          </tr>
                        ))}
                      </>
                    ) : (
                      <>
                        {currentPosts.length > 0 ? (
                          <>
                            {currentPosts.map((data, index) => {
                              return (
                                <tr key={index}>
                                  <td className="text-white">{indexOfFirstPost + index + 1}</td>
                                  <td>
                                    <div style={{ fontSize: "12px" }} className="text-white">
                                      {dayjs(data?.createdAt).format("DD/MM/YY")}
                                      <br />
                                      <span className="text-muted">
                                        {dayjs(data?.createdAt).format("HH:mm")}
                                      </span>
                                    </div>
                                  </td>
                                  <td>
                                    <div style={{ fontSize: "13px" }} className="text-white">
                                      {data?.senderId?.name || "Unknown"}
                                      <br />
                                      <span className="text-muted" style={{ fontSize: "11px" }}>
                                        {data?.senderType}
                                      </span>
                                    </div>
                                  </td>
                                  <td>
                                    <div style={{ fontSize: "13px" }} className="text-white">
                                      {data?.receiverId?.name || data?.receiverId?.shopName || "Unknown"}
                                      <br />
                                      <span className="text-muted" style={{ fontSize: "11px" }}>
                                        {data?.receiverType}
                                      </span>
                                    </div>
                                  </td>
                                  <td>
                                    <div className="d-flex align-items-center justify-content-center">
                                      {data?.giftId?.icon && (
                                        <img
                                          src={data?.giftId?.icon}
                                          alt="gift"
                                          width={30}
                                          height={30}
                                          className="me-2"
                                          style={{ borderRadius: "5px", objectFit: "cover" }}
                                        />
                                      )}
                                      <div style={{ fontSize: "13px" }} className="text-white">
                                        {data?.giftId?.name || "Deleted Gift"}
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <span className="badge bg-secondary">
                                      x{data?.quantity}
                                    </span>
                                  </td>
                                  <td>
                                    <span className="fw-bold text-white">
                                      {data?.totalAmount}
                                    </span>
                                    <br />
                                    <span className="text-muted" style={{ fontSize: "11px" }}>
                                      coins
                                    </span>
                                  </td>
                                  <td>
                                    <span className="text-warning fw-bold">
                                      {data?.platformCommission}
                                    </span>
                                    <br />
                                    <span className="text-muted" style={{ fontSize: "11px" }}>
                                      30%
                                    </span>
                                  </td>
                                  <td>
                                    <span className="text-success fw-bold">
                                      {data?.creatorEarning}
                                    </span>
                                    <br />
                                    <span className="text-muted" style={{ fontSize: "11px" }}>
                                      70%
                                    </span>
                                  </td>
                                  <td>
                                    <span className="badge bg-info">
                                      {data?.contentType}
                                    </span>
                                    {data?.contentId && (
                                      <div className="text-muted" style={{ fontSize: "10px" }}>
                                        ID: {data?.contentId.toString().slice(-6)}
                                      </div>
                                    )}
                                  </td>
                                  <td>
                                    <span className={`badge ${
                                      data?.status === "completed" ? "bg-success" :
                                      data?.status === "failed" ? "bg-danger" :
                                      "bg-warning"
                                    }`}>
                                      {data?.status}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </>
                        ) : (
                          <tr>
                            <td colSpan="11" className="text-center text-white">
                              No Transactions Found
                            </td>
                          </tr>
                        )}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Was wired with props the shared control does not have
                (totalPosts/paginate/currentPage), so it never rendered and only
                the first page of rows was reachable (2026-09-30). */}
            <Pagination
              component="div"
              type={"client"}
              count={total}
              totalData={total}
              serverPage={currentPage - 1}
              serverPerPage={rowsPerPage}
              onPageChange={(_e, p) => setCurrentPage(p + 1)}
              onRowsPerPageChange={(v) => {
                setRowsPerPage(parseInt(v, 10) || 25);
                setCurrentPage(1);
              }}
              rowsPerPageOptions={[25, 50, 100, 500]}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default GiftTransaction;
